import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, computed, signal, Signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';

import { CheckboxComponent } from '../checkbox/checkbox.component';
import { SearchComponent } from '../search/search.component';

export interface CheckboxListItem<T = any> {
  data: T;
  id: string;
  label: string;
  isSelected: boolean;
}

@Component({
  standalone: true,
  imports: [CommonModule, CheckboxComponent, SearchComponent],
  selector: 'nrc-checkbox-list',
  templateUrl: './checkbox-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxListComponent<T = any> implements OnInit {
  @Input({ required: true }) public labelKey!: keyof T;
  @Input({ required: true }) public valueKey!: keyof T;
  @Input() public searchable = true;
  @Input() public showSelectAll = true;
  @Input() public placeholder = 'Search...';
  @Input() public emptyMessage = 'No items found';

  @Output() public valueChange = new EventEmitter<string[]>();

  public items: WritableSignal<CheckboxListItem<T>[]> = signal<CheckboxListItem<T>[]>([]);

  public searchTerm = signal<string>('');

  public filteredItems = computed(() => {
    const items = this.items();
    const term = this.searchTerm().toLowerCase();

    if (!term) {
      return items;
    }

    return items.filter(item =>
      item.label.toLowerCase().includes(term)
    );
  });

  public isEveryFilteredItemSelected: Signal<boolean>;

  public isSomeButNotEveryFilteredItemSelected: Signal<boolean>;

  private rawItems: T[] = [];
  private selectedValues: string[] = [];

  /**
   * @constructor
   */
  constructor() {
    this.isEveryFilteredItemSelected = computed(() => {
      const filtered = this.filteredItems();

      return filtered.length > 0 && filtered.every(item => item.isSelected);
    });

    this.isSomeButNotEveryFilteredItemSelected = computed(() => {
      let someItemSelected = false;
      let everyItemSelected = true;

      const filtered = this.filteredItems();

      if (filtered.length > 0) {
        for (let i = 0; i < filtered.length && (!someItemSelected || everyItemSelected); i++) {
          if (filtered[i].isSelected) {
            someItemSelected = true;
          } else {
            everyItemSelected = false;
          }
        }
      } else {
        everyItemSelected = false;
      }

      return someItemSelected && !everyItemSelected;
    });
  }

  /**
   * Initialize component.
   */
  public ngOnInit(): void {
    this.transformItems();
  }

  /**
   * Set items input.
   * @param items Raw items to display.
   */
  @Input()
  public set itemsInput(items: T[]) {
    this.rawItems = items || [];
    this.transformItems();
  }

  /**
   * Set value input.
   * @param value Selected item IDs.
   */
  @Input()
  public set value(value: string[]) {
    this.selectedValues = value || [];
    this.transformItems();
  }

  /**
   * Transform raw items to internal format.
   */
  private transformItems(): void {
    const transformedItems = this.rawItems.map(item => ({
      data: item,
      id: String(item[this.valueKey]),
      label: String(item[this.labelKey]),
      isSelected: this.selectedValues.includes(String(item[this.valueKey]))
    }));

    this.items.set(transformedItems);
  }

  /**
   * Toggle single item selection.
   *
   * @param itemId The ID of the item to toggle.
   */
  public toggleItem(itemId: string): void {
    this.items.update(items =>
      items.map(item =>
        item.id === itemId
          ? { ...item, isSelected: !item.isSelected }
          : item
      )
    );

    this.emitSelection();
  }

  /**
   * Toggle all filtered items.
   */
  public toggleAll(): void {
    const shouldSelectAll = !this.isEveryFilteredItemSelected();

    const filteredIds = new Set(
      this.filteredItems().map(item => item.id)
    );

    this.items.update(items =>
      items.map(item =>
        filteredIds.has(item.id)
          ? { ...item, isSelected: shouldSelectAll }
          : item
      )
    );

    this.emitSelection();
  }

  /**
   * Emit selected IDs to parent.
   */
  private emitSelection(): void {
    const selectedIds = this.items()
      .filter(item => item.isSelected)
      .map(item => item.id);

    this.valueChange.emit(selectedIds);
  }

  /**
   * Handle search input change.
   *
   * @param term The search term entered by the user.
   */
  public onSearchChange(term: string): void {
    this.searchTerm.set(term);
  }
}
